import { useState, useEffect } from "react";
import styled from "styled-components";
import { HeaderNavigatorComponent } from "../HeaderNavigatorComponent";
import { LocationPicker } from "./LocationPicker";
import { setSelectedLocations, useAppDispatch, useAppSelector } from "@redux";
import { breakpoint, theme } from "@utilities";
import { DEFAULT_COUNTRY } from "@constants";
import { LocationSelections } from "@interfaces";

interface ILocationAreaProps {
    $isLight?: boolean;
    $productCount: number;
}
interface ILocationTextWrapperProps {
    $isLight?: boolean;
}

export const LocationArea = ({ $isLight = true, $productCount }: ILocationAreaProps) => {
    const dispatch = useAppDispatch();
    const selectedLocations = useAppSelector((state) => state.locationSearch.selectedLocations);
    const [popupOpen, setPopupOpen] = useState(false);
    const openPopup = () => setPopupOpen(true);
    const closePopup = () => setPopupOpen(false);
    // Local state to store selections from LocationPicker
    const [selections, setSelections] = useState<LocationSelections>({
        Country: selectedLocations.country || DEFAULT_COUNTRY,
        State: selectedLocations.state,
        Region: selectedLocations.region,
        Locality: selectedLocations.locality,
    });

    // Handler for cancel button
    const onCancel = () => {
        setSelections({
            Country: selectedLocations.country || DEFAULT_COUNTRY,
            State: selectedLocations.state,
            Region: selectedLocations.region,
            Locality: selectedLocations.locality,
        });
        closePopup();
    };

    // Handler for confirm button
    const onConfirm = () => {
        dispatch(
            setSelectedLocations({
                country: selections.Country,
                state: selections.State,
                region: selections.Region,
                locality: selections.Locality,
            }),
        );
        closePopup();
    };

    useEffect(() => {
        if (popupOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [popupOpen]);

    return (
        <>
            <LocationContainer onClick={openPopup}>
                <LocationTextWrapper $isLight={$isLight}>
                    <LocationContent>
                        <LocationIconLeft
                            src={
                                $isLight
                                    ? "/images/mynaui_location_dark.svg"
                                    : "/images/mynaui_location.svg"
                            }
                            alt="location logo"
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                    "/images/mynaui_location.svg";
                            }}
                        />
                        <LocationTextContainer>
                            <LocationText>
                                {[
                                    selectedLocations.state?.text,
                                    selectedLocations.region?.text,
                                    selectedLocations.locality?.text,
                                    selectedLocations.country?.text,
                                ]
                                    .filter(Boolean)
                                    .join(", ")}
                            </LocationText>
                        </LocationTextContainer>
                        <LocationIconRight
                            src={
                                $isLight
                                    ? "/images/Chevron_down_dark.svg"
                                    : "/images/Chevron_down.svg"
                            }
                            alt="chevron logo"
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                    "/images/Chevron_down.svg";
                            }}
                        />
                    </LocationContent>
                </LocationTextWrapper>
            </LocationContainer>
            {popupOpen && (
                <PopupOverlay onClick={closePopup}>
                    <PopupContent onClick={(e) => e.stopPropagation()}>
                        <div style={{ boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.15)" }}>
                            <HeaderNavigatorComponent
                                $onBackToHome={closePopup}
                                $productCount={$productCount}
                            />
                        </div>
                        <ContentWrapper>
                            <LocationPicker setSelections={setSelections} selections={selections} />
                        </ContentWrapper>
                        <Footer>
                            <CancelButton onClick={onCancel}>Cancel</CancelButton>
                            <ConfirmButton onClick={onConfirm}>Set Location</ConfirmButton>
                        </Footer>
                    </PopupContent>
                </PopupOverlay>
            )}
        </>
    );
};

const LocationContainer = styled.div`
    width: 100%;
    text-align: center;
    cursor: pointer;
`;

const LocationTextWrapper = styled.div<ILocationTextWrapperProps>`
    color: ${({ $isLight }) => ($isLight ? "#1A1A1A" : "#FFFFFF")};
`;

const LocationContent = styled.div`
    display: flex;
    align-items: center;
    width: auto;
    ${breakpoint.breakMobile`
        max-width: 200px;
    `}
    ${breakpoint.breakTablet`
        max-width: 100%;
    `}
    ${breakpoint.breakIpadPro`
        max-width: 250px;
    `}
    ${breakpoint.breakLarge`
        max-width: 100%;
    `}
    margin-left: 1px;
`;

const LocationIconLeft = styled.img`
    margin-right: 8px;
    ${breakpoint.breakIpadPro`
        width: 20px;
        height: 20px;
    `}
`;

const LocationIconRight = styled.img`
    margin-left: 8px;
    ${breakpoint.breakIpadPro`
        width: 16px;
        height: 16px;
    `}
`;

const LocationTextContainer = styled.div`
    overflow: hidden;
`;

const LocationText = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 18px;

    ${breakpoint.breakMobile`
        max-width: 100px;
    `}
    ${breakpoint.breakIpadPro`
        font-size: 16px;
    `}
`;

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100dvh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 3;
    ${breakpoint.breakTablet`
    display: flex;
    justify-content: center;
    align-items: center;
  `}
`;

const PopupContent = styled.div`
    position: relative;
    width: 100%;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    height: 100dvh;
    overflow: hidden;

    ${breakpoint.breakTablet`
    width: 700px;
    min-height: 350px;
    max-height: 600px;
    overflow-y: auto;
    border-radius: 8px;
    height: auto;
  `}
`;

const ContentWrapper = styled.div`
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    ${breakpoint.breakTablet`
    max-height: 480px;
  `}
`;

const Footer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 12px 18px;
    border-top: 1px solid #e0e0e0;
    background: #fff;
    z-index: 2;
    position: sticky;
    bottom: 0;

    ${breakpoint.breakTablet`
    position: static;
    width: 100%;
  `}
`;

const CancelButton = styled.button`
    flex: 1;
    margin-right: 8px;
    padding: 12px 0;
    border: 1px solid ${({ theme }) => theme.colors.main};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background: white;
    color: ${({ theme }) => theme.colors.button_text1};
    font-size: 16px;
    cursor: pointer;
`;

const ConfirmButton = styled.button`
    flex: 1;
    margin-left: 8px;
    padding: 12px 0;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background: linear-gradient(
        to right,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    color: ${theme.colors.button_text};
    font-size: 16px;
    cursor: pointer;
`;
